import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
    selector: 'app-note-header',
    templateUrl: './note-header.component.html',
    styleUrls: ['./note-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('slideDown', [
            transition(':enter', [
                style({ height: '0', opacity: 0, overflow: 'hidden' }),
                animate('200ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                style({ height: '*', opacity: 1, overflow: 'hidden' }),
                animate('200ms ease-in', style({ height: '0', opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class NoteHeaderComponent implements OnInit, OnChanges {
  @Input() noteId!: number;
  @Input() noteName!: string;
  @Input() mode!: 'create' | 'edit' | 'view';
  @Input() flagged: boolean = false;
  @Input() moduleLabel?: string;
  @Input() projectName?: string;
  @Input() projectId?: string;
  @Input() recordRoute: string | null = null;
  @Input() status?: string;
  @Input() priority: number = 0;

  @Output() flagToggle = new EventEmitter<boolean>();
  @Output() searchToggle = new EventEmitter<void>();
  @Output() searchInput = new EventEmitter<string>();
  @Output() deleteNote = new EventEmitter<void>();
  @Output() closeDrawer = new EventEmitter<void>();
  @Output() recordLinkClick = new EventEmitter<void>();
  @Output() priorityToggle = new EventEmitter<void>();
  @Output() markComplete = new EventEmitter<void>();
  @Output() exportPDF = new EventEmitter<void>();
  @Output() editNote = new EventEmitter<void>();

  isSearchOpen = false;
  searchQuery = '';

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Initialize component
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Force change detection when noteName input changes
    // This ensures the header title updates in real-time with OnPush strategy
    if (changes['noteName'] && !changes['noteName'].firstChange) {
      this.cdr.markForCheck();
    }
  }

  getDisplayTitle(): string {
    // Show noteName in all modes if available (create/edit/view)
    // Matches task module pattern: real-time display as user types
    // Fall back to placeholder only when noteName is empty
    return this.noteName || (this.noteId ? 'Update Note' : 'New Note');
  }

  onFlagToggle(): void {
    this.flagToggle.emit(!this.flagged);
  }

  onSearch(): void {
    this.isSearchOpen = !this.isSearchOpen;
    if (!this.isSearchOpen) {
      this.searchQuery = '';
    }
    this.searchToggle.emit();
  }

  onSearchInput(event: any): void {
    this.searchQuery = event.target.value;
    this.searchInput.emit(this.searchQuery);
  }

  onDelete(): void {
    this.deleteNote.emit();
  }

  onClose(): void {
    this.closeDrawer.emit();
  }

  onPriorityToggle(): void {
    this.priorityToggle.emit();
  }

  onMarkComplete(): void {
    this.markComplete.emit();
  }

  onExportPDF(): void {
    this.exportPDF.emit();
  }

  onEditNote(): void {
    this.editNote.emit();
  }

  getStatusBadgeStyles(): any {
    // Status color mapping
    const statusColors: any = {
      'open': '#2196F3',
      'in_progress': '#FF9800',
      'completed': '#4CAF50',
      'on_hold': '#9E9E9E',
      'cancelled': '#F44336'
    };

    const color = statusColors[this.status?.toLowerCase() || ''] || '#2196F3';

    return {
      'background-color': color,
      'color': '#ffffff'
    };
  }

  getFormattedStatus(): string {
    if (!this.status) return '';
    return this.status.replace(/_/g, ' ').toUpperCase();
  }

  get isHighPriority(): boolean {
    return this.priority === 3 || this.priority === 4;
  }

  get isStatusLocked(): boolean {
    const lockedStatuses = ['completed', 'cancelled', 'archived'];
    return lockedStatuses.includes(this.status?.toLowerCase() || '');
  }
}
